import React, { HTMLAttributes, ReactNode, forwardRef } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card padding variant */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Whether to show hover effect */
  hoverable?: boolean;
  /** Whether the card is clickable */
  clickable?: boolean;
  /** Custom header content */
  header?: ReactNode;
  /** Custom footer content */
  footer?: ReactNode;
  /** Children content */
  children?: ReactNode;
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Header title */
  title?: string;
  /** Header subtitle */
  subtitle?: string;
  /** Action buttons or elements */
  actions?: ReactNode;
  /** Children content (overrides title/subtitle) */
  children?: ReactNode;
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Body padding */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Children content */
  children?: ReactNode;
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Align footer content */
  align?: 'left' | 'center' | 'right' | 'between';
  /** Children content */
  children?: ReactNode;
}

/**
 * Card component with CGIC brand styling
 *
 * A flexible card container with optional header, body, and footer sections.
 * Supports hover effects and click interactions.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      padding = 'none',
      hoverable = false,
      clickable = false,
      header,
      footer,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'bg-white rounded-xl shadow-sm border border-neutral-200',
      'dark:bg-slate-800 dark:border-slate-700',
    ];

    const paddingClasses: Record<string, string> = {
      none: '',
      sm: 'p-3',
      md: 'p-5',
      lg: 'p-6',
    };

    const interactiveClasses = [];
    if (hoverable) {
      interactiveClasses.push(
        'transition-all duration-200',
        'hover:shadow-md hover:border-neutral-300',
        'dark:hover:shadow-dark-lg dark:hover:border-neutral-600'
      );
    }
    if (clickable) {
      interactiveClasses.push(
        'cursor-pointer',
        'active:scale-[0.99]'
      );
    }

    const combinedClassName = [
      ...baseClasses,
      paddingClasses[padding],
      ...interactiveClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        {header}
        {children}
        {footer}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header component
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, actions, className = '', children, ...props }, ref) => {
    const baseClasses = [
      'px-5 py-4 border-b border-neutral-200',
      'dark:border-neutral-700',
    ];

    const combinedClassName = [...baseClasses, className].join(' ');

    if (children) {
      return (
        <div ref={ref} className={combinedClassName} {...props}>
          {children}
        </div>
      );
    }

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        <div className="flex items-center justify-between">
          <div>
            {title && (
              <h3 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
                {subtitle}
              </p>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Body component
 */
export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ padding = 'md', className = '', children, ...props }, ref) => {
    const paddingClasses: Record<string, string> = {
      none: '',
      sm: 'px-4 py-3',
      md: 'px-5 py-4',
      lg: 'px-6 py-5',
    };

    const combinedClassName = [paddingClasses[padding], className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

/**
 * Card Footer component
 */
const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ align = 'right', className = '', children, ...props }, ref) => {
    const baseClasses = [
      'px-5 py-4 border-t border-neutral-200 bg-neutral-50',
      'dark:border-neutral-700 dark:bg-neutral-900/50',
    ];

    const alignClasses: Record<string, string> = {
      left: 'flex justify-start',
      center: 'flex justify-center',
      right: 'flex justify-end',
      between: 'flex justify-between',
    };

    const combinedClassName = [...baseClasses, alignClasses[align], 'gap-3', className].join(' ');

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export default Card;
